import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Module } from "@/constants";
import { useToast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
import { ApiError } from "@/services/api";
import {
  setAgreementPaperConfirmation,
  type AgreementPaperConfirmationFields,
  type AgreementPaperConfirmationKind,
} from "@/services/agreements";
import type { ReadAgreementDetail } from "@/schemas/agreement";

interface PaperConfirmationVariables {
  kind: AgreementPaperConfirmationKind;
  confirmed: boolean;
}

/** Pola odpowiedzi należące do danego rodzaju potwierdzenia. */
const FIELDS_BY_KIND: Record<
  AgreementPaperConfirmationKind,
  readonly (keyof AgreementPaperConfirmationFields)[]
> = {
  hr: [
    "agreement_hr_paper_confirmed",
    "agreement_hr_paper_confirmed_at",
    "agreement_hr_paper_confirmed_by",
    "agreement_hr_paper_confirmed_by_name",
  ],
};

/**
 * Wybiera z odpowiedzi tylko pola danego rodzaju: backend zwraca oba rodzaje, więc spóźniona
 * odpowiedź jednego checkboxa nie może nadpisać świeższego stanu drugiego. Trójka rekrutera
 * należy do przepływu skanów (`useAgreementPaperScans`) i nigdy nie jest tu nadpisywana.
 */
export const pickPaperConfirmationFields = (
  fields: AgreementPaperConfirmationFields,
  kind: AgreementPaperConfirmationKind,
): Partial<AgreementPaperConfirmationFields> =>
  Object.fromEntries(FIELDS_BY_KIND[kind].map((key) => [key, fields[key]]));

/**
 * Zapisuje potwierdzenie umowy papierowej (ASMMNT-83) i aktualizuje cache szczegółów w miejscu.
 * Celowo nie invaliduje klucza szczegółów po sukcesie: refetch zamieniłby całą stronę w loader.
 * Wyjątek to 409 (obywatelstwo zmieniło się pod nieświeżą stroną) — wtedy szczegóły są odświeżane.
 *
 * @param agreementId ULID umowy z trasy (ten sam, który jest kluczem cache szczegółów)
 * @returns mutacja TanStack Query z `{ kind, confirmed }` jako zmiennymi
 */
export const useAgreementPaperConfirmation = (agreementId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutationWithErrorHandling<
    AgreementPaperConfirmationFields,
    ApiError,
    PaperConfirmationVariables,
    unknown
  >({
    mutationFn: ({ kind, confirmed }) =>
      setAgreementPaperConfirmation(agreementId, kind, confirmed),
    onSuccess: (fields, { kind }) => {
      const kindFields = pickPaperConfirmationFields(fields, kind);
      queryClient.setQueryData<ReadAgreementDetail>(
        [Module.Agreements, agreementId],
        (previous) =>
          previous
            ? {
                ...previous,
                agreement: { ...previous.agreement, ...kindFields },
              }
            : previous,
      );
      // Lista umów ma się odświeżyć; szczegóły zostały już zaktualizowane powyżej.
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements],
        predicate: (query) => query.queryKey[1] !== agreementId,
      });
      toast({ title: t("agreements.success.paperConfirmationSaved") });
    },
    onError: (error) => {
      const title = t("agreements.errors.paperConfirmation");
      if (error.errorCode === 409) {
        queryClient.invalidateQueries({
          queryKey: [Module.Agreements, agreementId],
        });
        toast({
          title,
          description: t("agreements.errors.paperConfirmationNotForeign"),
          variant: "destructive",
        });
        return;
      }
      if (error.errorCode === 404) {
        toast({
          title,
          description: t("agreements.errors.paperConfirmationNotFound"),
          variant: "destructive",
        });
        return;
      }
      toast({
        title,
        description: getApiErrorDescription(error, t),
        variant: "destructive",
      });
    },
  });
};
